import { transformKaraokeConfigToTemplate } from '@src/services/videoTemplate/VideoTemplateTransformers';
import styled from 'styled-components';

import PresetPreviewText from '../PresetPreviewText';

import type { VideoTemplateConfigured } from './types';
import type { Preset } from '@src/services/karaoke/types';

interface VideoTemplatePreviewCardProps {
  videoTemplate: VideoTemplateConfigured;
}

export const VideoTemplatePreviewCard = ({
  videoTemplate,
}: VideoTemplatePreviewCardProps) => {
  const config = videoTemplate.configuration;

  const captionsConfig = config?.captionsConfiguration?.captionsConfig;
  const template = captionsConfig
    ? transformKaraokeConfigToTemplate(captionsConfig)
    : null;

  const introUrl = config?.introCardConfiguration?.enabled
    ? (config.introCardConfiguration.compositionTemplate?.thumbnail?.url ??
      null)
    : null;

  const endUrl = config?.endCardConfiguration?.enabled
    ? (config.endCardConfiguration.compositionTemplate?.thumbnail?.url ?? null)
    : null;

  const hasThumbnails = !!(introUrl || endUrl);

  return (
    <PreviewContainer>
      {hasThumbnails && template ? (
        /* Split layout: thumbnails on top, captions on bottom */
        <SplitSectionsWrapper>
          <ThumbnailRow>
            {introUrl && endUrl ? (
              <>
                <ThumbnailHalf>
                  <ThumbnailImage src={introUrl} alt="Intro Card" />
                </ThumbnailHalf>
                <ThumbnailHalf>
                  <ThumbnailImage src={endUrl} alt="End Card" />
                </ThumbnailHalf>
              </>
            ) : introUrl ? (
              <ThumbnailHalf>
                <ThumbnailImage src={introUrl} alt="Intro Card" />
              </ThumbnailHalf>
            ) : endUrl ? (
              <ThumbnailHalf>
                <ThumbnailImage src={endUrl} alt="End Card" />
              </ThumbnailHalf>
            ) : null}
          </ThumbnailRow>
          <CaptionsSection>
            <PresetPreviewText
              template={
                {
                  ...template,
                  previewText: videoTemplate.label,
                } as unknown as Preset
              }
            />
          </CaptionsSection>
        </SplitSectionsWrapper>
      ) : hasThumbnails && !template ? (
        /* Thumbnails only, no captions */
        introUrl && endUrl ? (
          <SplitBackgroundWrapper>
            <ThumbnailImage src={introUrl} alt="Intro Card" />
            <ThumbnailImage src={endUrl} alt="End Card" />
          </SplitBackgroundWrapper>
        ) : introUrl ? (
          <ThumbnailImage src={introUrl} alt="Intro Card" />
        ) : endUrl ? (
          <ThumbnailImage src={endUrl} alt="End Card" />
        ) : null
      ) : template ? (
        /* Captions only (current behavior) */
        <VerticalAlignWrapper>
          <PresetPreviewText
            template={
              {
                ...template,
                previewText: videoTemplate.label,
              } as unknown as Preset
            }
          />
        </VerticalAlignWrapper>
      ) : (
        /* No content at all */
        <PlaceholderContent>{videoTemplate.label}</PlaceholderContent>
      )}
    </PreviewContainer>
  );
};

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 6px;
  overflow: hidden;
  background: var(--surface);
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  background-color: var(--background);
`;

const SplitBackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;

  ${ThumbnailImage} {
    width: 50%;
    height: 100%;

    &:first-child {
      border-right: 1px solid var(--sidebar-active);
    }
  }
`;

const SplitSectionsWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ThumbnailRow = styled.div`
  display: flex;
  flex: 0 0 45%;
  overflow: hidden;
  border-bottom: 1px solid var(--sidebar-active);
`;

const ThumbnailHalf = styled.div`
  flex: 1;
  overflow: hidden;

  &:not(:last-child) {
    border-right: 1px solid var(--sidebar-active);
  }
`;

const CaptionsSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  pointer-events: none;
`;

const PlaceholderContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  text-align: center;
  font-size: var(--text-xs);
  color: var(--accent);
  background-color: var(--background);
`;

const VerticalAlignWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: none;
`;
